import type { Analysis } from '../types';

interface Props {
  item: Analysis;
  selected: boolean;
  onClick: () => void;
}

const RISK_LABEL: Record<string, string> = {
  low: 'Nízké',
  medium: 'Střední',
  high: 'Vysoké',
};

/**
 * Položka má reálné AI posouzení rizika pouze pokud doběhla finální analýza
 * (`final_decision` je vyplněna). Default `risk: "medium"` z `ingest.py` je jen
 * placeholder — proto se v takovém případě zobrazuje neutrální "Neposouzeno"
 * badge místo barevného risk badge.
 */
function hasRealRiskAssessment(item: Analysis): boolean {
  return Boolean(item.final_decision && item.analysis_complete);
}

function alertBadge(item: Analysis): { text: string; className: string } | null {
  if (!item.analysis_complete) {
    return { text: 'Rozpracováno', className: 'badge badge--incomplete' };
  }
  if (!item.final_decision) return null;
  const alert = item.final_decision.alert;
  if (alert === 'yes') {
    return { text: 'ALERT', className: 'badge badge--alert' };
  }
  if (alert === 'no') {
    return { text: 'Bez alertu', className: 'badge badge--ok' };
  }
  return { text: 'Placeholder', className: 'badge badge--placeholder' };
}

export function AnalysisCard({ item, selected, onClick }: Props) {
  const badge = alertBadge(item);
  const assessed = hasRealRiskAssessment(item);
  const cardRiskClass = assessed ? `analysis-card--risk-${item.risk}` : 'analysis-card--risk-unknown';
  const riskBadgeText = assessed ? (RISK_LABEL[item.risk] ?? item.risk) : 'Neposouzeno';
  const riskBadgeClass = assessed ? `risk-badge risk-badge--${item.risk}` : 'risk-badge risk-badge--unknown';
  const ariaRisk = assessed ? `riziko ${RISK_LABEL[item.risk] ?? item.risk}` : 'riziko zatím neposouzeno';

  return (
    <article
      className={`analysis-card ${cardRiskClass} ${selected ? 'analysis-card--selected' : ''}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); }}
      aria-selected={selected}
      aria-label={`${item.title} — ${ariaRisk}`}
    >
      <div className="analysis-card__header">
        <span className={riskBadgeClass}>{riskBadgeText}</span>
        {badge && <span className={badge.className}>{badge.text}</span>}
        <span className="analysis-card__area">{item.area}</span>
      </div>

      <h3 className="analysis-card__title">{item.title}</h3>

      <p className="analysis-card__summary">{item.summary}</p>

      {item.deadline && item.deadline !== 'Bez terminu' && (
        <p className="analysis-card__deadline">Termín: {item.deadline}</p>
      )}
    </article>
  );
}
